import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { config } from '../../utils/config';
import { blogHtmlPurifyConfig } from '../../utils/blogDomPurifyConfig';
import './Blog.css';

const BLOG_MEDIA_PUBLIC_BASE = (process.env.REACT_APP_OBJECT_STORAGE_PUBLIC_BASE_URL || '').trim().replace(/\/+$/, '');

const normalizeBlogImageUrl = (value) => {
  if (!value || typeof value !== 'string') return '';
  const raw = value.trim();
  if (!raw) return '';

  if (raw.startsWith('data:') || raw.startsWith('blob:')) {
    return raw;
  }

  if (raw.startsWith('//')) {
    return `https:${raw}`;
  }

  if (/^https?:\/\//i.test(raw)) {
    // Keep presigned URLs intact; stripping query params breaks access (403).
    if (/X-Amz-Signature=|X-Amz-Credential=/i.test(raw)) {
      return raw;
    }
    if (BLOG_MEDIA_PUBLIC_BASE) {
      try {
        const parsed = new URL(raw);
        const isLegacyAwsS3 = /amazonaws\.com$/i.test(parsed.hostname);
        if (isLegacyAwsS3) {
          const marker = '/vardaanwebsites/';
          const markerIndex = parsed.pathname.toLowerCase().indexOf(marker);
          if (markerIndex >= 0) {
            const relativePath = parsed.pathname.slice(markerIndex + marker.length).replace(/^\/+/, '');
            if (relativePath) {
              return `${BLOG_MEDIA_PUBLIC_BASE}/${relativePath}`;
            }
          }
        }
      } catch (_) {
        // Keep original URL if parsing fails.
      }
    }
    return raw;
  }

  const apiBase = (config.API_URL || '').replace(/\/+$/, '');
  if (!apiBase) return raw;
  if (raw.startsWith('/')) return `${apiBase}${raw}`;
  return `${apiBase}/${raw.replace(/^\/+/, '')}`;
};

function BlogPost() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const contentRef = useRef(null);
  const api = config.getFullApiUrl ? config.getFullApiUrl('/api/blogs') : `${config.API_URL}/api/blogs`;

  useEffect(() => {
    if (!slug) return;
    const fetchBlog = async () => {
      try {
        const res = await fetch(`${api}/${encodeURIComponent(slug)}`);
        const data = await res.json();
        if (data.success && data.blog) {
          setBlog({
            ...data.blog,
            cover_image: normalizeBlogImageUrl(data.blog.cover_image)
          });
        } else {
          setBlog(null);
        }
      } catch (e) {
        console.error('Failed to fetch blog', e);
        setBlog(null);
      } finally {
        setLoading(false);
      }
    };
    fetchBlog();
  }, [slug, api]);

  const safeBlogHtml = useMemo(() => {
    if (!blog?.content) return '';
    return DOMPurify.sanitize(blog.content, blogHtmlPurifyConfig);
  }, [blog?.content]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const slugifyId = (text, i) => {
    const safe = text.replace(/[^a-zA-Z0-9\s-]/g, '').replace(/\s+/g, '-').toLowerCase().slice(0, 40);
    return `section-${i}-${safe || 'heading'}`;
  };

  // Collect all "heading" nodes: h1-h4 and every <strong>/<b> (bold sentence = heading for "On this page")
  const getHeadingNodes = useCallback((doc) => {
    const list = [];
    const walk = (node) => {
      if (node.nodeType !== 1) return;
      const tag = node.tagName;
      if (tag === 'H1' || tag === 'H2' || tag === 'H3' || tag === 'H4') {
        list.push(node);
        return;
      }
      if (tag === 'STRONG' || tag === 'B') {
        const text = (node.textContent || '').trim();
        if (text.length > 0 && text.length < 400) list.push(node);
        return;
      }
      for (let i = 0; i < node.childNodes.length; i++) walk(node.childNodes[i]);
    };
    if (doc.body) walk(doc.body);
    return list;
  }, []);

  const headings = useMemo(() => {
    if (!safeBlogHtml) return [];
    const parser = new DOMParser();
    const doc = parser.parseFromString(safeBlogHtml, 'text/html');
    const nodes = getHeadingNodes(doc);
    return nodes.map((el, i) => {
      const text = (el.textContent || '').trim();
      const tag = el.tagName.toLowerCase();
      return { id: slugifyId(text, i), text, tag };
    }).filter((h) => h.text);
  }, [safeBlogHtml, getHeadingNodes]);

  const contentWithIds = useMemo(() => {
    if (!safeBlogHtml) return '';
    const parser = new DOMParser();
    const doc = parser.parseFromString(safeBlogHtml, 'text/html');
    doc.querySelectorAll('ul, ol').forEach((list) => {
      list.querySelectorAll(':scope > li').forEach((li) => {
        if ((li.textContent || '').replace(/\s/g, '') === '') {
          li.remove();
          return;
        }
        const blocks = li.querySelectorAll(':scope > p, :scope > div');
        blocks.forEach((block) => {
          while (block.firstChild) li.insertBefore(block.firstChild, block);
          block.remove();
        });
      });
      if (list.querySelectorAll(':scope > li').length === 0) list.remove();
    });
    const nodes = getHeadingNodes(doc);
    nodes.forEach((el, idx) => {
      const text = (el.textContent || '').trim();
      if (!text) return;
      el.id = slugifyId(text, idx);
    });
    doc.querySelectorAll('img').forEach((img) => {
      const src = img.getAttribute('src') || '';
      const normalized = normalizeBlogImageUrl(src);
      if (normalized) {
        img.setAttribute('src', normalized);
      }
    });
    return doc.body.innerHTML;
  }, [safeBlogHtml, getHeadingNodes]);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (loading) {
    return (
      <div className="blog-post-page">
        <div className="blog-loading">Loading…</div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="blog-post-page">
        <div className="blog-post-inner">
          <p className="blog-not-found">Blog post not found.</p>
          <Link to="/blog" className="blog-back-link">← Back to Blog</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-post-page">
      <header className="blog-post-hero" style={blog.cover_image ? { backgroundImage: `url(${blog.cover_image})` } : {}}>
        <div className="blog-post-hero-overlay" />
        <h1 className="blog-post-hero-title">{blog.title || 'Untitled'}</h1>
      </header>

      <div className="blog-post-inner">
        <nav className="blog-breadcrumb" aria-label="Breadcrumb">
          <Link to="/">Home</Link>
          <span className="blog-breadcrumb-sep">›</span>
          <Link to="/blog">Blog</Link>
          {blog.category && (
            <>
              <span className="blog-breadcrumb-sep">›</span>
              <span>{blog.category}</span>
            </>
          )}
          <span className="blog-breadcrumb-sep">›</span>
          <span className="blog-breadcrumb-current" title={blog.title}>
            {blog.title && blog.title.length > 40 ? blog.title.slice(0, 40) + '…' : blog.title}
          </span>
        </nav>

        <div className="blog-post-layout">
          <aside className="blog-post-sidebar">
            <div className="blog-post-toc">
              <div className="blog-post-toc-title">On this page</div>
              {headings.length > 0 ? (
                <ul>
                  {headings.map((h) => (
                    <li key={h.id} className={`blog-post-toc-item blog-post-toc-${h.tag}`}>
                      <button type="button" onClick={() => scrollToSection(h.id)} className="blog-post-toc-link">
                        {h.text}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="blog-post-toc-empty">No sections in this post.</p>
              )}
            </div>
          </aside>

          <main className="blog-post-main">
            <time className="blog-post-date">{formatDate(blog.updated_at || blog.created_at)}</time>
            {(blog.author || blog.category) && (
              <div className="blog-post-meta">
                {blog.author && <span className="blog-author">{blog.author}</span>}
                {blog.category && <span className="blog-category"> · {blog.category}</span>}
              </div>
            )}
            <div
              ref={contentRef}
              className="blog-post-content"
              dangerouslySetInnerHTML={{ __html: contentWithIds }}
            />
          </main>
        </div>
      </div>
    </div>
  );
}

export default BlogPost;
